import { useCallback } from 'react';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
  Users,
  MapPin,
  Clock,
  ShoppingCart,
  CheckCircle,
  Eye,
  Bell,
} from 'lucide-react';
import type { SupplierFormData, SiteAccessConfig } from './types';

/**
 * Supplier-side company that the user being onboarded can be linked to.
 * Either the new supplier itself (synthetic, id=0) or a sibling company
 * already in the supplier's group.
 */
export interface SupplierRelatedSite {
  id: number;
  name: string;
  code?: string;
  city?: string;
  country?: string;
  isNew?: boolean;
}

interface WizardStep4SiteAccessProps {
  formData: SupplierFormData;
  onInputChange: (field: keyof SupplierFormData, value: string | number | boolean | null | string[] | number[] | Record<string, unknown>) => void;
  previewMode: boolean;
  supplierSites: SupplierRelatedSite[];
}

export default function WizardStep4SiteAccess({
  formData,
  onInputChange,
  previewMode,
  supplierSites,
}: WizardStep4SiteAccessProps) {
  // CR-6696: the Primary Company chosen on Step 3 (group suppliers only) is
  // marked automatically here — no reselection needed. Unset defaults to the
  // new supplier itself (id 0), matching pre-CR behaviour.
  const isGroupSupplier = formData.is_part_of_group === true && !!formData.supplier_group_id;
  const primaryCompanyId = formData.primary_company_id ?? 0;

  // Build list of all contacts
  const allContacts = [
    { label: 'Primary Contact', name: `${formData.contact_first_name} ${formData.contact_last_name}`.trim(), email: formData.contact_email, position: formData.contact_job_title, isPrimary: true },
    ...formData.additional_contacts.map((c, i) => ({
      label: `Contact ${i + 2}`,
      name: `${c.first_name} ${c.last_name}`.trim(),
      email: c.email,
      position: c.job_title,
      isPrimary: false,
    })),
  ].filter(c => c.name.trim() || c.email);

  // Helper to get/set site access config
  const getAccess = useCallback((contactIdx: number, siteId: number): SiteAccessConfig => {
    const key = `${contactIdx}_${siteId}`;
    const saved = formData.site_access?.[key];
    // CR-6728: a draft saved before Approval was removed can still carry
    // role: 'approval'. Treat it as no role so it isn't stranded — there is no
    // longer a toggle that could clear it.
    if (saved) {
      return saved.role === 'approval' ? { ...saved, role: '' } : saved;
    }
    return {
      active: true,
      // CR-5878: Finance is not a relevant access module for supplier users,
      // so it's no longer offered or defaulted here (the toggle was removed
      // from the Access Modules list below).
      modules: { purchasing: true, quality: true },
      role: '',
      notifications: { procurement: false, quality: false },
    };
  }, [formData.site_access]);

  const setAccess = useCallback((contactIdx: number, siteId: number, update: Partial<SiteAccessConfig>) => {
    const key = `${contactIdx}_${siteId}`;
    const current = getAccess(contactIdx, siteId);
    const updated = { ...current, ...update };
    onInputChange('site_access', {
      ...(formData.site_access || {}),
      [key]: updated,
    });
  }, [formData.site_access, getAccess, onInputChange]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Supplier User Company Access</h2>
        <p className="text-muted-foreground">
          Configure site access, modules and notifications for each contact at <span className="font-medium">{formData.company_name || 'this supplier'}</span>.
        </p>
      </div>

      {allContacts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
          <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-muted-foreground">No contacts added yet. Go back to Step 3 to add contacts.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {allContacts.map((contact, cIndex) => (
            <div key={cIndex} className="border rounded-lg overflow-hidden shadow-sm">
              {/* Contact header */}
              <div className="bg-gray-50 px-5 py-3 border-b flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-xs">
                  {contact.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-gray-900 text-sm">{contact.name || 'Unnamed'}</span>
                  <span className="text-muted-foreground text-sm"> · {contact.email}</span>
                </div>
                {contact.isPrimary && <Badge className="bg-blue-600 text-white text-xs">Primary</Badge>}
              </div>

              {/* Site cards grid */}
              {supplierSites.length === 0 ? (
                <div className="p-5 text-sm text-muted-foreground">
                  No supplier companies available. Complete Step 1 (Supplier Group) and Step 2 (Supplier Details) first.
                </div>
              ) : (
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {supplierSites.map((site) => {
                    const access = getAccess(cIndex, site.id);
                    return (
                    <div key={site.id} className="border rounded-lg bg-white shadow-sm overflow-hidden">
                      {/* Site card header */}
                      <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <MapPin className="h-4 w-4 text-blue-600 shrink-0" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-sm truncate">{site.name}</p>
                              {site.isNew && (
                                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                                  New
                                </span>
                              )}
                              {/* CR-6696: auto-marked from the Step 3 Primary Company choice */}
                              {isGroupSupplier && site.id === primaryCompanyId && (
                                <Badge className="bg-blue-600 text-white text-[10px] px-1.5 py-0.5 shrink-0">
                                  Primary Company
                                </Badge>
                              )}
                            </div>
                            {site.code ? (
                              <p className="text-xs text-muted-foreground">{site.code}</p>
                            ) : (site.city || site.country) ? (
                              <p className="text-xs text-muted-foreground">
                                {[site.city, site.country].filter(Boolean).join(', ')}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {/* CR-7076: was a hardcoded checked+disabled Switch —
                              a control that could never be interacted with. */}
                          <span className="text-xs font-medium text-muted-foreground border rounded-full px-2 py-0.5">Linked</span>
                        </div>
                      </div>

                      <div className="p-4 space-y-3">
                        {/* Active at this company */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">Active at this company</span>
                          </div>
                          <Switch
                            checked={access.active !== false}
                            onCheckedChange={(checked) => setAccess(cIndex, site.id, { active: checked })}
                            disabled={previewMode}
                          />
                        </div>

                        <hr />

                        {/* Access Modules */}
                        <div className="space-y-2">
                          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Modules</span>
                          {[
                            { key: 'purchasing', label: 'Purchasing', icon: ShoppingCart },
                            { key: 'quality', label: 'Quality', icon: CheckCircle },
                            // CR-5878: Finance toggle removed — not relevant for supplier users.
                          ].map(({ key, label, icon: Icon }) => (
                            <div key={key} className="flex items-center justify-between py-0.5">
                              <div className="flex items-center gap-2">
                                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="text-sm">{label}</span>
                              </div>
                              <Switch
                                checked={access.modules?.[key] !== false}
                                onCheckedChange={(checked) => setAccess(cIndex, site.id, {
                                  modules: { ...(access.modules || {}), [key]: checked },
                                })}
                                disabled={previewMode}
                              />
                            </div>
                          ))}
                        </div>

                        <hr />

                        {/* CR-7076 (ADMIN-MODEL): the per-site "User Role"
                            section is retired — the Administrator option wrote
                            a flag no backend check reads (admin authority is
                            the user-level role). Read Only survives as the
                            Access Override. access.role now only ever carries
                            'read_only' or ''. */}
                        <div className="space-y-2">
                          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Access Override</span>
                          <div className="flex items-center justify-between py-0.5">
                            <div className="flex items-center gap-2">
                              <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-sm">Read Only</span>
                            </div>
                            <Switch
                              checked={access.role === 'read_only'}
                              onCheckedChange={(checked) => setAccess(cIndex, site.id, { role: checked ? 'read_only' : '' })}
                              disabled={previewMode}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            View everything at this company, change nothing. Overrides their user role here.
                          </p>
                        </div>

                        <hr />

                        {/* Notification Emails */}
                        <div className="space-y-2">
                          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Notifications</span>
                          {[
                            { key: 'procurement', label: 'Procurement', icon: Bell },
                            { key: 'quality', label: 'Quality', icon: Bell },
                          ].map(({ key, label, icon: Icon }) => (
                            <div key={key} className="flex items-center justify-between py-0.5">
                              <div className="flex items-center gap-2">
                                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="text-sm">{label}</span>
                              </div>
                              <Switch
                                checked={access.notifications?.[key] === true}
                                onCheckedChange={(checked) => setAccess(cIndex, site.id, {
                                  notifications: { ...(access.notifications || {}), [key]: checked },
                                })}
                                disabled={previewMode}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
